import React, { useState } from 'react';
import {
  Search, Filter, MapPin, Star, Heart, MessageSquare, 
  Bookmark, Eye, Award, CheckCircle, Clock, DollarSign,
  Users, Briefcase, Globe, Leaf, Shield, Zap, Target,
  ChevronDown, TrendingUp, Calendar, Send, Plus
} from 'lucide-react';

const FreelanceSearch = ({ company }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilters, setSelectedFilters] = useState({
    skills: [],
    location: 'all',
    experience: 'all',
    certification: 'all',
    availability: 'all',
    hourlyRate: 'all'
  });
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);

  // Mock data pour les freelances
  const freelances = [
    {
      id: 1,
      name: "Camille Dupont",
      title: "Designer UX/UI Éco-responsable",
      avatar: "CD",
      rating: 4.9,
      reviewsCount: 127,
      completedProjects: 89,
      hourlyRate: 65,
      location: "Paris, France",
      availability: "Disponible",
      responseTime: "< 2h",
      skills: ["UX/UI Design", "Figma", "Design System", "Eco-design", "Accessibilité"],
      certifications: ["Label NR", "OPQUAST", "Carbon Literacy"],
      description: "Designer passionnée par l'éco-conception et l'accessibilité. J'aide les entreprises responsables à créer des expériences numériques durables et inclusives.",
      topSkill: "Eco-design",
      projectTypes: ["App Mobile", "Site Web", "Design System"],
      languages: ["Français", "Anglais"],
      successRate: 98,
      onTimeDelivery: 96,
      clientSatisfaction: 4.9,
      portfolio: [
        { title: "App GreenBank", image: null, type: "Mobile App" },
        { title: "Site EcoTech", image: null, type: "Website" }
      ],
      recentWork: "Application mobile pour une banque éthique",
      gradient: "from-blue-500 to-cyan-500",
      badges: ["Top Rated", "Eco-Certified", "Fast Response"],
      specialties: ["Design durable", "UX éco-responsable", "Accessibilité web"]
    },
    {
      id: 2,
      name: "Thomas Bernard",
      title: "Développeur Full-Stack Engagé",
      avatar: "TB",
      rating: 4.8,
      reviewsCount: 203,
      completedProjects: 156,
      hourlyRate: 75,
      location: "Lyon, France",
      availability: "Disponible dans 2 semaines",
      responseTime: "< 1h",
      skills: ["React", "Node.js", "TypeScript", "MongoDB", "AWS"],
      certifications: ["B-Corp Dev", "Green IT", "AWS Certified"],
      description: "Développeur passionné par les technologies durables. Je crée des solutions web performantes et éco-conçues pour des entreprises à impact positif.",
      topSkill: "React",
      projectTypes: ["Web App", "API", "Dashboard"],
      languages: ["Français", "Anglais", "Allemand"],
      successRate: 97,
      onTimeDelivery: 94,
      clientSatisfaction: 4.8,
      portfolio: [
        { title: "Dashboard RSE", image: null, type: "Web App" },
        { title: "API GreenData", image: null, type: "Backend" }
      ],
      recentWork: "Plateforme de monitoring carbone",
      gradient: "from-green-500 to-emerald-500",
      badges: ["Expert Level", "Green Tech", "Reliable"],
      specialties: ["Green IT", "Performance web", "Architecture scalable"]
    },
    {
      id: 3,
      name: "Sophie Martin",
      title: "Data Scientist & Analyste Impact",
      avatar: "SM",
      rating: 4.9,
      reviewsCount: 94,
      completedProjects: 67,
      hourlyRate: 80,
      location: "Remote",
      availability: "Disponible",
      responseTime: "< 3h",
      skills: ["Python", "Machine Learning", "Data Analysis", "Tableau", "R"],
      certifications: ["Carbon Trust", "Data Science Ethics", "ISO 26000"],
      description: "Spécialiste en analyse de données d'impact environnemental et social. J'aide les organisations à mesurer et optimiser leur empreinte RSE.",
      topSkill: "Machine Learning",
      projectTypes: ["Data Analysis", "Predictive Models", "Dashboards"],
      languages: ["Français", "Anglais", "Espagnol"],
      successRate: 99,
      onTimeDelivery: 98,
      clientSatisfaction: 4.9,
      portfolio: [
        { title: "Modèle Prédictif Carbone", image: null, type: "ML Model" },
        { title: "Dashboard Impact", image: null, type: "Analytics" }
      ],
      recentWork: "Analyse d'impact social pour une ONG",
      gradient: "from-purple-500 to-pink-500",
      badges: ["Impact Expert", "ML Specialist", "Certified"],
      specialties: ["Impact measurement", "ESG Analytics", "Predictive modeling"]
    }
  ];

  const filteredFreelances = freelances.filter(freelance => {
    const matchesSearch = freelance.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         freelance.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         freelance.skills.some(skill => skill.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesSearch;
  });

  const FreelanceCard = ({ freelance }) => (
    <div className="bg-white rounded-2xl border border-gray-100 hover:shadow-2xl transition-all duration-300 overflow-hidden group hover:scale-[1.02]">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-4">
            <div className={`w-16 h-16 bg-gradient-to-r ${freelance.gradient} rounded-2xl flex items-center justify-center text-white font-bold text-xl shadow-lg`}>
              {freelance.avatar}
            </div>
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <h3 className="font-bold text-gray-900 group-hover:text-green-600 transition-colors duration-200">
                  {freelance.name}
                </h3>
                <div className="flex space-x-1">
                  {freelance.badges.includes("Top Rated") && (
                    <span className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center">
                      <Star className="w-4 h-4 text-yellow-500" />
                    </span>
                  )}
                  {freelance.badges.includes("Eco-Certified") && (
                    <span className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                      <Leaf className="w-4 h-4 text-green-500" />
                    </span>
                  )}
                </div>
              </div>
              <p className="text-gray-600 text-sm font-medium">{freelance.title}</p>
              <div className="flex items-center mt-1 space-x-4">
                <div className="flex items-center">
                  <Star className="w-4 h-4 text-yellow-400 mr-1" />
                  <span className="text-sm font-medium text-gray-700">{freelance.rating}</span>
                  <span className="text-xs text-gray-500 ml-1">({freelance.reviewsCount})</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="w-4 h-4 text-gray-400 mr-1" />
                  <span className="text-xs text-gray-500">{freelance.location}</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200">
              <Bookmark className="w-5 h-5 text-gray-600" />
            </button>
            <div className={`w-3 h-3 rounded-full ${
              freelance.availability === 'Disponible' ? 'bg-green-400' : 'bg-orange-400'
            }`}></div>
          </div>
        </div>

        {/* Description */}
        <p className="text-gray-600 text-sm mb-4 line-clamp-3">{freelance.description}</p>

        {/* Metrics */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center">
            <div className="text-lg font-bold text-gray-900">{freelance.hourlyRate}€</div>
            <div className="text-xs text-gray-500">par heure</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-blue-600">{freelance.completedProjects}</div>
            <div className="text-xs text-gray-500">projets</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-green-600">{freelance.successRate}%</div>
            <div className="text-xs text-gray-500">succès</div>
          </div>
        </div>

        {/* Skills */}
        <div className="mb-4">
          <div className="flex flex-wrap gap-2">
            {freelance.skills.slice(0, 4).map((skill) => (
              <span key={skill} className="px-3 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-lg">
                {skill}
              </span>
            ))}
            {freelance.skills.length > 4 && (
              <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-lg">
                +{freelance.skills.length - 4}
              </span>
            )}
          </div>
        </div>

        {/* Certifications */}
        <div className="mb-4">
          <div className="flex items-center space-x-2">
            <Award className="w-4 h-4 text-yellow-500" />
            <div className="flex space-x-2">
              {freelance.certifications.slice(0, 2).map((cert) => (
                <span key={cert} className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-lg">
                  {cert}
                </span>
              ))}
              {freelance.certifications.length > 2 && (
                <span className="text-xs text-gray-500">+{freelance.certifications.length - 2}</span>
              )}
            </div>
          </div>
        </div>

        {/* Availability & Response */}
        <div className="flex items-center justify-between mb-4 text-sm">
          <div className="flex items-center space-x-2">
            <Clock className="w-4 h-4 text-green-500" />
            <span className="text-gray-600">{freelance.availability}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Zap className="w-4 h-4 text-blue-500" />
            <span className="text-gray-600">Répond en {freelance.responseTime}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex space-x-3">
          <button className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white py-3 rounded-xl font-semibold hover:from-green-600 hover:to-emerald-600 transition-all duration-200 flex items-center justify-center group">
            <MessageSquare className="w-4 h-4 mr-2 group-hover:translate-x-1 transition-transform duration-200" />
            Contacter
          </button>
          <button className="px-4 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors duration-200">
            <Eye className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center">
            <Users className="w-8 h-8 mr-3 text-green-500" />
            Trouver des Freelances
          </h1>
          <p className="text-gray-600">
            Découvrez des talents certifiés et engagés qui partagent vos valeurs d'impact positif.
          </p>
        </div>
        
        <div className="mt-4 lg:mt-0 flex items-center space-x-4">
          <button className="flex items-center space-x-2 px-4 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors duration-200">
            <Bookmark className="w-4 h-4 text-gray-600" />
            <span className="text-gray-700">Mes favoris</span>
          </button>
          <button className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-xl font-semibold hover:from-green-600 hover:to-emerald-600 transition-all duration-200 flex items-center">
            <Plus className="w-5 h-5 mr-2" />
            Publier un projet
          </button>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        {/* Search Bar */}
        <div className="relative mb-6">
          <Search className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 transform -translate-y-1/2" />
          <input
            type="text"
            placeholder="Rechercher par nom, compétence, ou spécialité..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-6 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg"
          />
        </div>

        {/* Quick Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          {[
            { label: "Disponible maintenant", icon: Clock, active: true },
            { label: "Top rated", icon: Star },
            { label: "Certifié éco", icon: Leaf },
            { label: "Réponse rapide", icon: Zap },
            { label: "Expert", icon: Award }
          ].map((filter, index) => (
            <button
              key={index}
              className={`flex items-center space-x-2 px-4 py-2 rounded-xl border transition-all duration-200 ${
                filter.active 
                  ? 'bg-green-100 border-green-300 text-green-700' 
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <filter.icon className="w-4 h-4" />
              <span className="text-sm font-medium">{filter.label}</span>
            </button>
          ))}
        </div>

        {/* Advanced Filters Toggle */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center space-x-2 text-green-600 hover:text-green-700 font-medium"
        >
          <Filter className="w-4 h-4" />
          <span>Filtres avancés</span>
          <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showFilters ? 'rotate-180' : ''}`} />
        </button>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Localisation</label>
                <select className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent">
                  <option>Toutes</option>
                  <option>Paris</option>
                  <option>Lyon</option>
                  <option>Remote</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Expérience</label>
                <select className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent">
                  <option>Tous niveaux</option>
                  <option>Débutant</option>
                  <option>Intermédiaire</option>
                  <option>Expert</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Taux horaire</label>
                <select className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent">
                  <option>Tous</option>
                  <option>- de 50€</option>
                  <option>50-75€</option>
                  <option>+ de 75€</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Certification</label>
                <select className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent">
                  <option>Toutes</option>
                  <option>Label NR</option>
                  <option>B-Corp</option>
                  <option>Carbon Trust</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Disponibilité</label>
                <select className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent">
                  <option>Toutes</option>
                  <option>Immédiate</option>
                  <option>Cette semaine</option>
                  <option>Ce mois</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            {filteredFreelances.length} freelances trouvés
          </h2>
          <p className="text-gray-600 text-sm">Triés par pertinence et engagement éthique</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <select className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent">
            <option>Plus pertinents</option>
            <option>Mieux notés</option>
            <option>Prix croissant</option>
            <option>Prix décroissant</option>
          </select>
        </div>
      </div>

      {/* IA Recommendations */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100">
        <div className="flex items-start space-x-4">
          <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-gray-900 mb-2">Recommandations IA pour {company.name}</h3>
            <p className="text-gray-600 text-sm mb-4">
              Basées sur vos projets précédents et vos valeurs B-Corp, voici les profils qui correspondent le mieux à vos besoins.
            </p>
            <div className="flex space-x-3">
              {filteredFreelances.slice(0, 3).map((freelance) => (
                <div key={freelance.id} className="flex items-center space-x-2 bg-white rounded-lg px-3 py-2 border border-purple-200">
                  <div className={`w-8 h-8 bg-gradient-to-r ${freelance.gradient} rounded-lg flex items-center justify-center text-white font-bold text-sm`}>
                    {freelance.avatar}
                  </div>
                  <span className="text-sm font-medium text-gray-900">{freelance.name}</span>
                  <span className="text-xs text-purple-600">96% match</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Freelances Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredFreelances.map((freelance) => (
          <FreelanceCard key={freelance.id} freelance={freelance} />
        ))}
      </div>

      {/* Load More */}
      {filteredFreelances.length > 0 && (
        <div className="text-center">
          <button className="px-8 py-3 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors duration-200">
            Voir plus de freelances
          </button>
        </div>
      )}

      {/* Empty State */}
      {filteredFreelances.length === 0 && (
        <div className="text-center py-12">
          <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucun freelance trouvé</h3>
          <p className="text-gray-600 mb-6">Essayez d'ajuster vos critères de recherche ou contactez-nous pour des recommandations personnalisées.</p>
          <button className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-xl font-semibold hover:from-green-600 hover:to-emerald-600 transition-all duration-200">
            Publier un projet
          </button>
        </div>
      )}
    </div>
  );
};

export default FreelanceSearch;